import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Menu, X, Calendar } from 'lucide-react';

const NAV_LINKS = [
  { path: '/', label: 'Home' },
  { path: '/batting-tunnels', label: 'Batting Tunnels' },
  { path: '/soccer-futsal', label: 'Soccer/Futsal' },
  { path: '/tournaments', label: 'Tournaments' },
  { path: '/parties', label: 'Parties & Events' },
  { path: '/facility', label: 'Facility' },
  { path: '/about', label: 'About' },
  { path: '/faq', label: 'FAQ' },
  { path: '/contact', label: 'Contact' },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  const { isAdmin } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white ${
        scrolled ? 'shadow-md' : 'shadow-sm'
      }`}
    >
      <div className="max-w-[1340px] mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-24 md:h-32">
          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0" data-testid="nav-logo">
            <img
              src="/propel-logo.png"
              alt="Propel Sports"
              className="h-16 sm:h-20 md:h-28 w-auto object-contain"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-0.5">
            {NAV_LINKS.map(link => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                className={`px-3 py-1.5 rounded text-[13px] font-medium transition-all duration-200 ${
                  loc.pathname === link.path
                    ? 'text-[#F26522]'
                    : 'text-[#444] hover:text-[#F26522]'
                }`}
              >
                {link.label}
              </Link>
            ))}
            {isAdmin && (
              <Link
                to="/admin"
                data-testid="nav-link-admin"
                className={`px-3 py-1.5 rounded text-[13px] font-medium transition-all ${
                  loc.pathname.startsWith('/admin') ? 'text-[#F26522]' : 'text-[#F26522]/70 hover:text-[#F26522]'
                }`}
              >
                Admin
              </Link>
            )}
          </div>

          {/* CTA + Mobile Toggle */}
          <div className="flex items-center gap-3">
            <a
              href="https://propelsports.myesoftplanner.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-book-now"
              className="hidden sm:flex items-center gap-1.5 bg-[#F26522] hover:bg-[#D9551A] text-white px-5 py-2.5 rounded-md font-semibold text-sm transition-all duration-200 hover:shadow-lg hover:shadow-orange-500/25"
            >
              Book Now
            </a>
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen(!open)}
              className="lg:hidden p-2 text-[#333] hover:text-[#F26522] transition-colors"
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`lg:hidden overflow-hidden transition-all duration-300 ${open ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="bg-white border-t border-gray-100 px-4 py-4 space-y-1 shadow-lg">
          {NAV_LINKS.map(link => (
            <Link
              key={link.path}
              to={link.path}
              className={`block px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                loc.pathname === link.path
                  ? 'text-[#F26522] bg-orange-50'
                  : 'text-[#333] hover:text-[#F26522] hover:bg-gray-50'
              }`}
            >
              {link.label}
            </Link>
          ))}
          {isAdmin && (
            <Link to="/admin" className="block px-4 py-2.5 rounded-lg text-sm font-medium text-[#F26522] hover:bg-orange-50">
              Admin Panel
            </Link>
          )}
          <a href="https://propelsports.myesoftplanner.com" target="_blank" rel="noopener noreferrer"
            className="block mt-2 text-center bg-[#F26522] hover:bg-[#D9551A] text-white px-5 py-3 rounded-lg font-semibold text-sm">
            Book Now
          </a>
        </div>
      </div>
    </nav>
  );
}
